import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { BottomTabBar } from '@/components/BottomTabBar';
import { SubmissionFlow } from '@/components/SubmissionFlow';
import { useAuth } from '@/contexts/AuthContext';
import NeumorphicSwitch from '@/components/ui/NeumorphicSwitch';
import { useQueryClient } from '@tanstack/react-query';
import { CreateContentModal } from '@/components/CreateContentModal';
import { useTour } from '@/contexts/TourContext';
import { useFeedContext } from '@/contexts/FeedContext';
import FeedToggle from '@/components/ui/FeedToggle';
import AuthButton from '@/components/AuthButton';
import logoLight from '@/components/logo/logo light.png';
import logoDark from '@/components/logo/logo dark.png';
import { useTheme } from 'next-themes';

const AppShell = () => {
  const [showSubmission, setShowSubmission] = React.useState(false);
  const [showSnapModal, setShowSnapModal] = React.useState(false);
  const [showDroolModal, setShowDroolModal] = React.useState(false);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { tourStatus, startTour } = useTour();
  const { showFeedToggle, activeFeed, setActiveFeed, hideChrome } = useFeedContext();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [navHidden, setNavHidden] = React.useState(false);
  const lastScrollY = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      // RAF throttle: process at most one scroll event per animation frame
      if (tickingRef.current) return;
      tickingRef.current = true;
      requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        if (currentScrollY <= 50) {
          setNavHidden(false);
        } else if (currentScrollY > lastScrollY.current + 5) {
          setNavHidden(true);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setNavHidden(false);
        }
        lastScrollY.current = currentScrollY;
        tickingRef.current = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine if we're in "eat-out" mode based on current route and query params
  const searchParams = new URLSearchParams(location.search);
  const modeParam = searchParams.get('mode');
  
  // Unified: all main pages use query params for mode
  const isEatOut = modeParam !== 'eat-in';

  // Define routes where the FAB should be visible (visible to all users)
  const allowedFabRoutes = ['/boards', '/feed', '/global-100', '/drips'];
  const showFab = !authLoading && (allowedFabRoutes.includes(location.pathname) || location.pathname.startsWith('/profile') || location.pathname.startsWith('/restaurant') || location.pathname === '/account/settings');

  // Immersive mode for full-screen feeds
  const isImmersiveRoute = location.pathname === '/drools' || location.pathname === '/feed';

  const handleModeToggle = () => {
    const newMode = isEatOut ? 'eat-in' : 'eat-out';

    if (location.pathname === '/boards') {
      const currentTab = searchParams.get('tab') || 'charts';
      navigate(`/boards?tab=${currentTab}&mode=${newMode}`);
    } else if (location.pathname === '/global-100') {
      navigate(`/global-100?mode=${newMode}`);
    } else {
      navigate(`${location.pathname}?mode=${newMode}`);
    }
  };

  const handleSubmissionClose = () => {
    setShowSubmission(false);
    queryClient.invalidateQueries({ queryKey: ['chartFeed'] });
    queryClient.invalidateQueries({ queryKey: ['whatsNew'] });
  };

  // Auto-start tour for authenticated first-time users on Boards page
  // Uses state machine: only starts when tourStatus is 'idle'
  useEffect(() => {
    console.log('[Tour Trigger] Evaluating conditions:', {
      pathname: location.pathname,
      isUser: !!user,
      userId: user?.id,
      isAuthLoading: authLoading,
      tourStatus,
    });

    // Simple state machine check - only start from 'idle' state
    if (location.pathname !== '/boards' || !user || authLoading || tourStatus !== 'idle') {
      return;
    }

    console.log('[Tour Trigger] All conditions met. Setting timer...');

    const timer = setTimeout(() => {
      console.log('[Tour Trigger] setTimeout fired. Starting tour...');
      
      // --- DEFENSIVE CHECK ---
      // This prevents a tour restart even if a race condition briefly sets tourStatus to 'idle'.
      const userStorageKey = `fork_it_tour_completed_${user.id}`;
      const storedCompletion = localStorage.getItem(userStorageKey);
      if (storedCompletion) {
        console.log('[Tour Trigger] Defensive check: Tour already completed in localStorage. Aborting start.');
        return; // Abort if localStorage already shows completion
      }
      // --- END DEFENSIVE CHECK ---
      
      // Verify the first tour element exists before starting
      const firstElement = document.querySelector('[data-tour="mode-switch"]');
      if (firstElement) {
        console.log('[Tour Trigger] Defensive check passed. Starting tour.');
        startTour();
      } else {
        console.warn('[Tour Trigger] First tour element not found in the DOM. Aborting tour start.');
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [location.pathname, user?.id, authLoading, tourStatus, startTour]);

  return (
    <div className={`min-h-screen relative ${isImmersiveRoute && isDark ? 'bg-black' : 'bg-background'}`}>
      {/* Hidden anchor for tour welcome step */}
      <div data-tour="welcome" className="hidden" aria-hidden="true" />
      {/* Main App Header */}
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-transform duration-300 ease-in-out ${(navHidden && !(isImmersiveRoute && isDark)) || (location.pathname === '/feed' && hideChrome) ? '-translate-y-full' : 'translate-y-0'} ${isImmersiveRoute && isDark ? 'bg-black/30 backdrop-blur-md' : 'bg-background/80 backdrop-blur-md'}`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        <div className={`flex items-center h-16 px-4 ${
          isImmersiveRoute && isDark
            ? 'border-b border-white/10'
            : 'border-b border-border'
        }`}>
          {/* Left: Logo */}
          <img src={isDark ? logoDark : logoLight} alt="Fork It" className="h-7 w-auto" />

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right: Toggle */}
          <div className="flex items-center gap-3">
            {showFeedToggle ? (
              <FeedToggle value={activeFeed} onValueChange={setActiveFeed} />
            ) : !isImmersiveRoute && (
              (location.pathname === '/boards' && searchParams.get('tab') !== 'lists') ||
              location.pathname === '/global-100'
            ) ? (
              <NeumorphicSwitch
                key={`${location.pathname}-${searchParams.get('tab') || ''}-${modeParam || 'default'}`}
                isEatOut={isEatOut}
                onToggle={handleModeToggle}
              />
            ) : null}
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main
        style={isImmersiveRoute ? {} : {
          paddingTop: 'calc(env(safe-area-inset-top) + 64px)',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 68px)',
        }}
      >
        <Outlet />
      </main>

      {/* Modals */}
      <SubmissionFlow 
        isOpen={showSubmission} 
        onClose={handleSubmissionClose} 
      />

      <CreateContentModal
        isOpen={showSnapModal}
        onClose={() => setShowSnapModal(false)}
        initialType="snap"
      />

      <CreateContentModal
        isOpen={showDroolModal}
        onClose={() => setShowDroolModal(false)}
        initialType="drool"
      />

      {/* Bottom Tab Bar — portaled to document.body so position:fixed is always relative to the viewport */}
      {createPortal(
        <BottomTabBar
          showFab={showFab}
          isGuest={!user}
          isImmersive={isImmersiveRoute && isDark}
          onSubmitDish={() => setShowSubmission(true)}
          onPostSnap={() => setShowSnapModal(true)}
          onPostDrool={() => setShowDroolModal(true)}
        />,
        document.body
      )}

    </div>
  );
};

export default AppShell;